import $ from 'jquery';
import KeyUtils from 'chairisher/util/key';
import { getCreditCardLogoUrl, getDeleteSavedCardsUrl } from 'chairisher/context/payment';

/**
 * Component that handles interaction with saved credit cards
 *
 * @param {Object=} settings The object containing the settings for the credit card
 * @param {jQuery} settings.$el The jQuery element that represents the credit card in the DOM
 * @param {boolean=} settings.canDeselect Indicates if the credit card can be deselected once selected
 *   (like a checkbox) or not (like a radio button)
 * @param {boolean=} settings.canDelete Indicates if the credit card can be deleted or not
 * @param {boolean=} settings.canSelect Indicates if the credit card is selectable or not
 * @param {string=} settings.cardId The Stripe ID representing the credit card
 * @param {boolean=} settings.isDisabled Indicates if the credit card is disabled or not
 * @param {boolean=} settings.isSelected Indicates if the credit card is selected or not
 * @param {string=} settings.targetSelector Selector to choose an element in the DOM that should be updated
 *   with the card id when selected
 */
class CreditCard {
    constructor(settings = {}) {
        this.$el = settings.$el || null;
        this.canDelete = settings.canDelete || false;
        this.canDeselect = settings.canDeselect || false;
        this.canSelect = settings.canSelect || false;
        this.cardId = settings.cardId || '';
        this.isDisabled = settings.isDisabled || false;
        this.isSelected = settings.isSelected || false;
        this.targetSelector = settings.targetSelector || null;
    }

    /**
     * Initializes the credit card component by setting up its state and bindings.
     */
    initialize() {
        this.toggleSelectable(this.canSelect);
        this.toggleSelected(this.isSelected);
        this.bind();

        if (this.isSelected) {
            this.updateTarget();
        }

        this.$el.data('creditcard', this);
    }

    /**
     * CSS classes that represent the various states a credit card can have
     *
     * @enum
     */
    static classes = {
        disabled: 'disabled',
        selectable: 'selectable',
        selected: 'selected',
    };

    /**
     * Selectors that can be used to select a credit card from the DOM
     *
     * @enum
     */
    static selectors = {
        disabled: '.js-disabled',
        selectable: '.js-selectable',
        selected: '.js-selected',
    };

    /**
     * Deletes the credit card; side effects include disabling/enabling the card while the request is in flight
     *
     * @returns {Promise}
     * @fires CreditCard#creditcard:deletesuccess
     * @fires CreditCard#creditcard:deletefail
     */
    delete() {
        this.disable();

        return $.ajax({
            data: { card_id: this.getCardId() },
            method: 'POST',
            url: getDeleteSavedCardsUrl(),
        })
            .done((jqXHR) => this.$el.trigger('creditcard:deletesuccess', [jqXHR]))
            .fail((jqXHR) => this.$el.trigger('creditcard:deletefail', [jqXHR]))
            .always(() => this.enable());
    }

    /**
     * Toggles the "selected" state for the credit card to be "deselected"
     */
    deselect() {
        this.toggleSelected(false);
    }

    /**
     * Toggles the "disabled" state for the credit card to be "disabled"
     */
    disable() {
        this.toggleDisabled(true);
    }

    /**
     * Toggles the "disabled" state for the credit card to be "enabled"
     */
    enable() {
        this.toggleDisabled(false);
    }

    /**
     * @returns {jQuery} The jQuery element representing the credit card
     */
    getEl() {
        return this.$el;
    }

    /**
     * @returns {string} The Stripe ID representing the credit card
     */
    getCardId() {
        return this.cardId;
    }

    /**
     * Toggles the "selected" state for the credit card to be "selected"
     */
    select() {
        this.toggleSelected(true);
    }

    /**
     * Toggles the "disabled" state for the credit card
     *
     * @param {boolean=} isDisabled Optional argument indicating if the credit card should be disabled or not;
     *   toggles to the opposite of the current disabled state if omitted.
     */
    toggleDisabled(isDisabled) {
        const disabledClass = CreditCard.classes.disabled;
        this.$el.toggleClass(disabledClass, isDisabled);
        this.isDisabled = this.$el.hasClass(disabledClass);
    }

    /**
     * Toggles the "selected" state for the credit card
     *
     * @param {boolean=} isSelected Optional argument indicating if selection should be toggled on or off;
     *   toggles to the opposite of the current selection state if omitted.
     */
    toggleSelected(isSelected) {
        const selectedClass = CreditCard.classes.selected;
        this.$el.toggleClass(selectedClass, isSelected);
        this.isSelected = this.$el.hasClass(selectedClass);
    }

    /**
     * Updates the value of `this.targetSelector` if set
     */
    updateTarget() {
        if (this.targetSelector) {
            $(this.targetSelector).val(this.isSelected ? this.cardId : '');
        }
    }

    /**
     * Binds actions related to the DOM element
     *
     * @fires CreditCard#creditcard:deselected
     * @fires CreditCard#creditcard:selected
     */
    bind() {
        if (this.canSelect) {
            // support clicking
            this.$el.on('click', () => {
                if (this.isSelected && this.canDeselect) {
                    this.deselect();
                    this.$el.trigger('creditcard:deselected');
                } else {
                    this.select();
                    this.$el.trigger('creditcard:selected');
                }
                this.updateTarget();
            });

            // support keyboard selection
            this.$el.on('keypress', (e) => {
                const validSelectionKeys = [KeyUtils.KeyCodes.Enter, KeyUtils.KeyCodes.Space];
                if (validSelectionKeys.includes(e.which)) {
                    if (!this.isSelected) {
                        this.select();
                        this.$el.trigger('creditcard:selected');
                    } else if (this.isSelected && this.canDeselect) {
                        this.deselect();
                        this.$el.trigger('creditcard:deselected');
                    }
                    this.updateTarget();
                }
            });
        }

        if (this.canDelete) {
            // deletion
            this.$el.on('click', '.js-action-delete', (e) => {
                e.preventDefault();
                this.delete();
            });
        }
    }

    /**
     * Modifies the credit card element to be selectable (or not) via click or keyboard
     *
     * @param {boolean} isSelectable True indicates the DOM element should be selectable
     */
    toggleSelectable(isSelectable) {
        this.$el.toggleClass(CreditCard.classes.selectable, isSelectable);

        // make selectable via keyboard
        if (isSelectable) {
            this.$el.attr('tabindex', 0);
        } else {
            this.$el.removeAttr('tabindex');
        }
    }

    /**
     * Creates a new jQuery representation of the credit card for use in the DOM.
     *
     * @param {Object} data
     * @param {string} data.brand The type of credit card (i.e. Visa, Mastercard, etc.)
     * @param {string} data.card_id The Stripe id representing the card
     * @param {string} data.last4 The 4 digits of the credit card
     * @param {string} data.exp_month The 1 or 2 digit month the card expires in
     * @param {string} data.exp_year The 2 digit year the card expires in
     *
     * @returns {jQuery}
     */
    static marshalCreditCard(data) {
        const cardName = data.brand === 'American Express' ? 'Amex' : data.brand;

        const $creditCard = $($('#js-template-saved-card').html());
        $creditCard.attr('data-card-id', data.card_id);
        $creditCard.find('.js-saved-card-logo').attr({
            alt: data.brand,
            src: getCreditCardLogoUrl(cardName),
        });
        $creditCard.find('.js-saved-card-logo').removeAttr('srcset'); // Prevent lazy loading; no need for sizes w/SVG
        $creditCard.find('.js-saved-card-type-last4').text(`${cardName}...${data.last4}`);
        $creditCard.find('.js-saved-card-exp-month').text(data.exp_month);
        $creditCard.find('.js-saved-card-exp-year').text(data.exp_year);
        $creditCard.find('.js-action-delete').attr('href', getDeleteSavedCardsUrl());

        return $creditCard;
    }
}

export default CreditCard;
